'use client';

import { fetchGoodsList } from '@/api/product';
import {
  IGoodsListPageInitialData,
  IGroupInfo,
  IProductListApiParams,
  IProductListApiResponse,
  IProductListItem,
} from '@/api/types/product';
import GoodsFilter from '@/app/goods-list/components/GoodsFilter';
import ProductList from '@/app/goods-list/components/ProductList';
import SortFilterBar from '@/app/goods-list/components/SortFilterBar';
import { JumpTarget } from '@/components/MagicJump';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { DEFAULT_SORT_VALUE } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { transformFilterParams } from '@/utils/goods-list-utils';
import { getRouteCnName } from '@/utils/route-metadata';
import { isEmpty } from '@/utils/tools';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ActivePageListBanner from './ActivePageListBanner';
import ActivePageListNav from './ActivePageListNav';

interface NavItem {
  name: string;
  path: string;
  image: string;
  type?: 'default' | 'main'; // 移动端专用
}

// Banner元素接口
interface BannerElement {
  span: number;
  image: string;
  imageAlt?: string;
  linkTo: JumpTarget;
}

// Banner行类型（一行可能包含多个元素）
type BannerRow = BannerElement[];

interface PageData {
  name: string;
  groupId: string | number;
  seoInfo?: any;
  navList?: NavItem[];
  banner?: BannerRow[]; // 二维数组
}

interface MobWifeStyleGlassesClientProps {
  deviceType: EPlatform;
  initialData: IGoodsListPageInitialData & PageData;
  searchParams?: {
    sort?: string;
    filter?: string;
    adp?: string;
    page?: string;
  };
}

export default function MobWifeStyleGlassesClient({
  deviceType,
  searchParams,
  initialData,
}: MobWifeStyleGlassesClientProps) {
  // const pathname = usePathname();
  const router = useRouter();

  const { trackEvent } = useReporting();
  const t = useTranslations('activity');
  const pathname = usePathname();
  // 参数
  const limit = 36;
  // 基础状态（来自服务端）
  const [groupInfo, setGroupInfo] = useState<IGroupInfo>(initialData.group);
  const [listData, setListData] = useState<IProductListItem[]>(initialData.list);
  const [total, setTotal] = useState(initialData.total);
  const [allTotal, setAllTotal] = useState(initialData.all_total);
  const [imageRule, setImageRule] = useState(initialData.image_rule);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);

  const currency = useStore((state) => state.currency);
  const [showFilter, setShowFilter] = useState(false);

  // 使用useMemo稳定currentFilter的引用，避免无限循环
  const currentFilter = useMemo<Record<string, any>>(() => {
    try {
      return searchParams?.filter ? JSON.parse(searchParams.filter) : {};
    } catch (error) {
      logger.warn('解析筛选参数失败:', searchParams?.filter, error);
      return {};
    }
  }, [searchParams?.filter]);
  // 解析当前URL参数
  const currentSort = searchParams?.sort || DEFAULT_SORT_VALUE;
  const currentPage = parseInt(searchParams?.page || '1', 10);

  // 弹窗模式下的临时筛选状态
  const [tempFilter, setTempFilter] = useState<Record<string, any>>({});
  const initialDataRef = useRef(initialData);
  const hasTrackedView = useRef(false);
  const startTimeRef = useRef(Date.now());
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isTextOverflow, setIsTextOverflow] = useState(false);

  // UI相关状态
  const [loading, setLoading] = useState(false);
  const [noMoreShow, setNoMoreShow] = useState(initialData.noMoreShow);
  const [infiniteScrollPage, setInfiniteScrollPage] = useState(currentPage);
  const [column, setColumn] = useState(2);
  const [noData, setNoData] = useState(initialData.noData);

  const seoDescRef = useRef<HTMLDivElement>(null);
  const { getFromRouterInfo } = useRouteInfo();

  const isWeb = deviceType === EPlatform.WEB;
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  // 判断页面类型
  const isNewin = groupInfo?.page_template?.id === 3;
  const isAcc = groupInfo?.page_template?.id === 5;
  // 页面浏览埋点
  useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      startTimeRef.current = Date.now();
      SDThemePageView();
    }
    return () => {
      // SDThemePageLeave(); // 事件已作废，保留锁
      hasTrackedView.current = false;
    };
  }, []);

  /**
   * @TrackType sensorsdata
   * @EventType ThemePage_View
   * @EventDesc 专题页面浏览
   */
  const SDThemePageView = () => {
    try {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();

      trackEvent(
        {
          eventName: 'ThemePage_View',
          properties: {
            page_name: getRouteCnName(pathname),
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('SDThemePageView error:', e);
    }
  };
  const ThemePageListSortBtn_Click = (sortName: string) => {
    try {
      trackEvent(
        {
          eventName: 'ThemePageSortBtn_Click',
          properties: {
            page_name: getRouteCnName(pathname),
            module_name: '卖货版位',
            sort_name: sortName,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('ThemePageSortBtn_Click error:', e);
    }
  };

  const themePageListMktExp = useCallback((index: number, params: any) => {
    try {
      const sdEventName = 'ThemePageProductPits_Exp';
      const sdEventParams = {
        page_name: getRouteCnName(pathname),
        module_name: '卖货版位',
        tab_type: params.tab_type,
        pits_index: index,
        product_sku: params.product_sku,
        business_sku: params.business_sku,
        shop_sku: params.shop_sku,
        product_id: params.product_id,
        product_name: params.product_name,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);
  const themePageListClick = useCallback((index: number, params: any) => {
    try {
      const sdEventName = 'ThemePageProductPits_Click';
      const sdEventParams = {
        page_name: getRouteCnName(pathname),
        module_name: '卖货版位',
        tab_type: params.tab_type,
        pits_index: index,
        product_sku: params.product_sku,
        business_sku: params.business_sku,
        shop_sku: params.shop_sku,
        product_id: params.product_id,
        product_name: params.product_name,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);
  // 滚动到列表区域
  const scrollToListSection = useCallback(() => {
    const listSection = document.querySelector('.list-section');
    const num = isWeb ? 100 : 44;
    if (listSection) {
      const rect = listSection.getBoundingClientRect();
      const offsetTop = window.pageYOffset + rect.top - num;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  }, []);
  // 处理排序变化 - 通过URL变化触发服务端重渲染
  const handleSortChange = (sortValue: { value: string | number; sortName: string }) => {
    const newSort = sortValue.value.toString();
    const newUrl = buildNewUrl({ sort: newSort, page: 1 });

    setIsGlobalLoading(true);
    scrollToListSection();
    router.push(newUrl, { scroll: false });

    // 埋点
    // sdProductListPageSortBtnClick(sortValue.sortName || DEFAULT_SORT_VALUE);
  };
  const handleFilterClose = useCallback(() => {
    // 关闭弹窗时重置临时筛选状态
    setTempFilter(currentFilter);
    setShowFilter(false);
  }, [currentFilter]);
  // 筛选相关处理
  const handleFilterClick = useCallback(() => {
    // 打开筛选弹窗时，重置临时筛选状态为当前筛选状态
    setTempFilter(currentFilter);
    setShowFilter(true);
    // !isWeb && SDProductListPageBtn_Click('FILTER');
  }, [
    currentFilter,
    // SDProductListPageBtn_Click,
    isWeb,
  ]);
  /**
   * @TrackType sensorsdata
   * @EventType ThemePageNavigationTab_Exp
   * @EventDesc 专题页导航标签曝光
   */
  const handleNavigationTabExp = () => {
    try {
      const properties = {
        page_name: getRouteCnName(pathname),
      };
      trackEvent(
        {
          eventName: 'ThemePageNavigationTab_Exp',
          properties,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('handleNavigationTabExp error:', e);
    }
  };
  /**
   * @TrackType sensorsdata
   * @EventType ThemePageNavigationTab_Click
   * @EventDesc 专题页导航标签点击
   */
  const handleNavigationTabClick = (tabName: string) => {
    try {
      const properties = {
        page_name: getRouteCnName(pathname),
        tab_name: tabName,
      };
      trackEvent(
        {
          eventName: 'ThemePageNavigationTab_Click',
          properties,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('handleNavigationTabClick error:', e);
    }
  };
  // 构建新URL的工具函数
  const buildNewUrl = useCallback(
    (updates: { sort?: string; filter?: Record<string, any>; page?: number }) => {
      const newSearchParams = new URLSearchParams(searchParams || {});

      // 处理排序参数
      if (updates.sort !== undefined) {
        if (updates.sort && updates.sort !== DEFAULT_SORT_VALUE) {
          newSearchParams.set('sort', updates.sort);
        } else {
          newSearchParams.delete('sort');
        }
      }

      // 处理筛选参数
      if (updates.filter !== undefined) {
        newSearchParams.delete('filter');
        if (!isEmpty(updates.filter)) {
          const cleanFilter = {};
          Object.entries(updates.filter).forEach(([key, value]) => {
            if (value && key !== 'init') {
              cleanFilter[key] = value as string;
            }
          });
          if (!isEmpty(cleanFilter)) {
            newSearchParams.set('filter', JSON.stringify(cleanFilter));
          }
        }
      }

      // 处理页码参数
      if (updates.page !== undefined) {
        if (updates.page === 1) {
          newSearchParams.delete('page');
        } else {
          newSearchParams.set('page', updates.page.toString());
        }
      }

      const queryString = newSearchParams.toString();
      return `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
    },
    [searchParams],
  );
  // 列数变化
  const handleColumnChange = useCallback(
    (newColumn: number) => {
      setColumn(newColumn);
      if (listData.length > 0) {
        scrollToListSection();
      }
    },
    [scrollToListSection, listData],
  );
  // 处理筛选变化 - 通过URL变化触发服务端重渲染
  const handleFilterChange = (newFilterResult: Record<string, any>) => {
    const newUrl = buildNewUrl({ filter: newFilterResult, page: 1 });

    setIsGlobalLoading(true);
    scrollToListSection();
    router.push(newUrl, { scroll: false });
  };

  /**
   * @TrackType sensorsdata
   * @EventType ThemePageBanner_Exp
   * @EventDesc 专题页Banner曝光
   */
  const handleBannerExp = (params: any) => {
    try {
      const properties = {
        page_name: getRouteCnName(pathname),
        ...params,
      };
      trackEvent(
        {
          eventName: 'ThemePageBanner_Exp',
          properties,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('handleBannerExp error:', e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType ThemePageBanner_Click
   * @EventDesc 专题页Banner点击
   */
  const handleBannerClick = (params: any) => {
    try {
      const properties = {
        page_name: getRouteCnName(pathname),
        ...params,
      };
      trackEvent(
        {
          eventName: 'ThemePageBanner_Click',
          properties,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('handleBannerClick error:', e);
    }
  };

  const handleFilterApply = useCallback(() => {
    handleFilterChange(tempFilter);
    setShowFilter(false);
  }, [tempFilter, handleFilterChange]);
  // 处理分页变化 - 通过URL变化触发服务端重渲染
  const handlePageChange = (page: number) => {
    const newUrl = buildNewUrl({ page });

    setIsGlobalLoading(true);
    scrollToListSection();
    router.push(newUrl, { scroll: false });
  };
  // 处理无限滚动到底部（仅WAP端）
  const handleReachBottom = async () => {
    if (isWeb || loading || noMoreShow) return;

    const nextPage = infiniteScrollPage + 1;
    setInfiniteScrollPage(nextPage);
    setLoading(true);

    try {
      // 构建API参数
      let sortParam = currentSort;
      if (sortParam === '' || sortParam === DEFAULT_SORT_VALUE) {
        sortParam = '';
      }
      const transformedFilter = transformFilterParams(currentFilter);

      const apiParams: IProductListApiParams = {
        currency: currency.name,
        limit,
        page: nextPage,
        order: sortParam,
        in_stock: 1,
        is_sale: 1,
        filter: transformedFilter,
        append_correlation: 1,
        show_adv: 1,
      };
      // if (useGroupPath) {
      //   apiParams.group_path = String(listId);
      // } else {
      //   apiParams.group_id = Number(listId);
      // }
      apiParams.group_id = Number(initialData.groupId);

      const response = (await fetchGoodsList(apiParams)) as unknown as {
        code: number;
        data: IProductListApiResponse;
      };

      if (response.code === 200) {
        const { list = [], all_total } = response.data as IProductListApiResponse;

        // 追加数据
        setListData((prev) => [...prev, ...list]);
        const _noMoreShow = all_total <= nextPage * 36;
        setNoMoreShow(_noMoreShow);
      }
    } catch (error) {
      logger.error('无限滚动加载失败:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleFilterValueChange = useCallback(
    (filterId: string, value: any, isPopup: boolean) => {
      if (isPopup) {
        // 弹窗模式：更新临时筛选状态，提供立即的视觉反馈
        setTempFilter((prev) => ({ ...prev, [filterId]: value }));
      } else {
        // 非弹窗模式：立即应用筛选
        const newFilterResult = { ...(currentFilter || {}), [filterId]: value };
        handleFilterChange(newFilterResult);
      }
    },
    [currentFilter, handleFilterChange],
  );
  // 渲染筛选组件
  const renderGoodsFilter = useCallback(
    (isPopup = false) => {
      // 弹窗模式使用临时筛选状态，非弹窗模式使用当前筛选状态
      const filterValues = isPopup ? tempFilter : currentFilter;

      return (
        <GoodsFilter
          onClear={() => {
            if (isPopup) {
              // 弹窗模式：清空临时筛选状态
              setTempFilter({});
            } else {
              // 非弹窗模式：立即清空筛选
              handleFilterChange({});
            }
          }}
          values={filterValues}
          onValueChange={(filterId, value) => handleFilterValueChange(filterId, value, isPopup)}
          isWeb={isWeb}
          isPopup={isPopup}
          // sdProductListPageFilterSubmitBtnClick={sdProductListPageFilterSubmitBtnClick}
          {...(isPopup ? { onApply: handleFilterApply } : {})}
        />
      );
    },
    [
      tempFilter,
      currentFilter,
      handleFilterValueChange,
      handleFilterApply,
      handleFilterChange,
      isWeb,
    ],
  );
  // 计算筛选总数
  const filterTotal = Object.keys(currentFilter).reduce((_total, key) => {
    const value = currentFilter[key];
    if (['temple', 'frame_width'].includes(key)) return _total;
    if (Array.isArray(value)) return _total + value.length;
    return value ? _total + 1 : _total;
  }, 0);
  useEffect(() => {
    // 避免首次重复设置初始化数据
    if (initialDataRef.current === initialData) return;
    initialDataRef.current = initialData;
    const newGroupInfo = initialData.group;
    setGroupInfo(newGroupInfo);
    setListData(initialData.list);
    setTotal(initialData.total);
    setAllTotal(initialData.all_total);
    setImageRule(initialData.image_rule);
    setNoData(initialData.noData);
    setNoMoreShow(initialData.noMoreShow);

    // 重置无限滚动页码为当前页码
    setInfiniteScrollPage(currentPage);

    // 服务端数据更新后，关闭页面loading
    setIsGlobalLoading(false);
  }, [initialData, currentPage, setGroupInfo]);
  // 检查文本是否溢出
  useEffect(() => {
    if (initialData.seoInfo?.pc_pure_text && seoDescRef.current) {
      const textHeight = 210; // 10行高度 (21px * 10)
      const curHeight = seoDescRef.current.clientHeight;
      if (curHeight > textHeight) {
        setIsTextOverflow(true);
        setIsTextExpanded(false);
      } else {
        setIsTextOverflow(false);
      }
    }
  }, [initialData.seoInfo]);
  useEffect(() => {
    if (!isWeb) {
      // * 直接set不生效，估计有别的地方设置为true，宏任务延迟一下
      setTimeout(() => {
        setIsShowFooterNav(false);
      }, 50);
    }
  }, [isWeb, setIsShowFooterNav]);
  return (
    <div className="w-full min-h-screen bg-[#f5f6f7] ">
      {/* PC端布局 */}
      <WebOnly>
        <div className="flex min-h-[calc(100vh-107px)]">
          {/* 左侧筛选区域预留 - 如果需要筛选功能可以在这里添加 */}
          {
            <div className="max-[920px]:hidden">
              <div className="w-[304px] left-0 z-[2] h-screen sticky top-[0px] transition-transform duration-10">
                {/* 增加overscrollBehavior属性，阻止滚动事件向父级容器传播，防止滚动穿透，导致出现左侧滚动到底之后，右侧商品列表开始滚动 */}
                <div
                  className="h-full bg-white overflow-y-auto show-scrollbar-when-hover"
                  style={{ overscrollBehavior: 'contain' }}
                >
                  <p className="pt-[56px] text-20 leading-[24px] font-bb pb-24 pl-48">
                    {t('filter')}
                    {filterTotal > 0 ? `(${filterTotal})` : ''}
                  </p>
                  <div className="pl-48 pr-24 pb-[350px]">{renderGoodsFilter(false)}</div>
                  <div className="pc-filter-bottom-anchor" />
                </div>
              </div>
            </div>
          }

          {/* 右侧内容区域 */}
          <div className="flex-1 px-[50px] lg:px-[53px] pt-[17px]">
            {/* 面包屑导航 */}
            {initialData.name && (
              <div className="mb-[20px] flex items-center gap-1 text-[14px]">
                <Link href="/" className="text-primary-black hover:underline">
                  Home
                </Link>
                <span className="text-primary-black">&nbsp;/&nbsp;</span>
                <span className="text-primary-black">{initialData.name}</span>
              </div>
            )}

            {/* 导航标签 */}
            {initialData.navList && initialData.navList.length > 0 && (
              <ActivePageListNav
                list={initialData.navList}
                deviceType={deviceType}
                onExp={handleNavigationTabExp}
                onClick={handleNavigationTabClick}
              />
            )}

            {/* Banner */}
            {initialData.banner && initialData.banner.length > 0 && (
              <ActivePageListBanner
                list={initialData.banner}
                deviceType={deviceType}
                pageName={undefined} // PC端不显示面包屑
                onExp={handleBannerExp}
                onClick={handleBannerClick}
              />
            )}

            {/* 主要内容区域 */}
            <section className="list-section">
              {/* 排序筛选栏 */}
              <SortFilterBar
                listId={initialData.groupId}
                currentSort={currentSort}
                onSortChange={handleSortChange}
                filterTotal={filterTotal}
                onFilterClick={handleFilterClick}
                showColumnControl
                column={column}
                onColumnChange={handleColumnChange}
                isAcc={isAcc}
                groupInfo={groupInfo}
                isNewin={isNewin}
                isWeb
                total={total}
                wrapperClassName="!bg-[#f5f6f7]"
                onSortBtnClick={() => {
                  ThemePageListSortBtn_Click('SORT BY');
                }}
              />

              {/* 商品列表 */}
              <ProductList
                listData={listData}
                total={total}
                allTotal={allTotal}
                noData={noData}
                loading={loading}
                isWeb
                column={column}
                isAcc={isAcc}
                imageRule={imageRule}
                imageHoverRule={[2, 1, 3]}
                infiniteScrollPage={infiniteScrollPage}
                limit={limit}
                handleReachBottom={handleReachBottom}
                page={currentPage}
                handlePageChange={handlePageChange}
                handleProductClick={themePageListClick}
                handleProductInView={themePageListMktExp}
                // sdProductListPageADExp={sdProductListPageADExp}
                // sdProductListPageADClick={sdProductListPageADClick}
                // SDProductListPageTryon_Click={SDProductListPageTryon_Click}
                isCrowdRecommendation={false}
                currentFilter={currentFilter}
              />
              {(initialData.seoInfo?.h_one || initialData.seoInfo?.pc_pure_text) && (
                <div className="p-[32px_24px] mb-[56px] text-center bg-white rounded-[8px]">
                  {initialData.seoInfo.h_one && (
                    <h1 className="text-left mb-[15px] text-[24px] font-bb">
                      {initialData.seoInfo.h_one}
                    </h1>
                  )}

                  {initialData.seoInfo.pc_pure_text && (
                    <div className="relative">
                      <div
                        ref={seoDescRef}
                        className={`text-left text-[14px] leading-[22px] whitespace-pre-wrap ${
                          !isTextExpanded && isTextOverflow ? 'max-h-[210px] overflow-hidden' : ''
                        }`}
                        dangerouslySetInnerHTML={{ __html: initialData.seoInfo.pc_pure_text }}
                      />

                      {/* 渐变遮罩 */}
                      {!isTextExpanded && isTextOverflow && (
                        <div className="absolute bottom-0 left-0 right-0 h-[32px] bg-gradient-to-t from-white to-transparent" />
                      )}

                      {/* 展开/收起按钮 */}
                      {isTextOverflow && (
                        <div className="mt-[25px] flex justify-center">
                          <button
                            onClick={() => setIsTextExpanded(!isTextExpanded)}
                            className={`w-[44px] h-[20px] bg-white rounded-[10px] border border-[#D0D4DB]
                        flex items-center justify-center cursor-pointer transition-transform
                        ${isTextExpanded ? 'rotate-0' : 'rotate-180'}`}
                          >
                            <CollapseArrowDownOutlined />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>

        {/* PC端筛选弹窗 */}

        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="left"
          width="482px"
          round={false}
          title={t('filters')}
          bodyClassName="!pt-0 !px-32"
          sx={{
            '& .popup-title': {
              textAlign: 'left',
              border: 'none',
              paddingInline: '32px !important',
              paddingBlock: '26px !important',
              lineHeight: '24px',
              '& .popup-title-text': {
                fontSize: '20px !important',
              },
            },
          }}
        >
          {renderGoodsFilter(true)}
        </Popup>
      </WebOnly>

      {/* 移动端布局 */}
      <WapOnly>
        <div className="w-full bg-white">
          {/* 导航标签 */}
          {initialData.navList && initialData.navList.length > 0 && (
            <ActivePageListNav
              list={initialData.navList}
              deviceType={deviceType}
              onExp={handleNavigationTabExp}
              onClick={handleNavigationTabClick}
            />
          )}

          {/* Banner */}
          {initialData.banner && initialData.banner.length > 0 && (
            <ActivePageListBanner
              list={initialData.banner}
              deviceType={deviceType}
              pageName={initialData.name} // 移动端显示面包屑
              onExp={handleBannerExp}
              onClick={handleBannerClick}
            />
          )}

          {/* 商品列表 */}
          {/* 主要内容区域 */}
          <section className="list-section">
            {/* 排序筛选栏 */}
            <SortFilterBar
              listId={initialData.groupId}
              currentSort={currentSort}
              onSortChange={handleSortChange}
              filterTotal={filterTotal}
              onFilterClick={handleFilterClick}
              showColumnControl
              column={column}
              onColumnChange={handleColumnChange}
              isAcc={isAcc}
              groupInfo={groupInfo}
              isNewin={isNewin}
              isWeb={false}
              inAppH5={inAppH5}
              total={total}
              onSortBtnClick={() => {
                // SDProductListPageBtn_Click('SORT BY');
              }}
            />

            {/* 商品列表 */}
            <ProductList
              listData={listData}
              total={total}
              allTotal={allTotal}
              noData={noData}
              loading={loading}
              isWeb={false}
              column={column}
              isAcc={isAcc}
              imageRule={imageRule}
              imageHoverRule={[2, 1, 3]}
              infiniteScrollPage={infiniteScrollPage}
              limit={limit}
              handleReachBottom={handleReachBottom}
              page={currentPage}
              handlePageChange={handlePageChange}
              handleProductClick={themePageListClick}
              handleProductInView={themePageListMktExp}
              // sdProductListPageADExp={sdProductListPageADExp}
              // sdProductListPageADClick={sdProductListPageADClick}
              // SDProductListPageTryon_Click={SDProductListPageTryon_Click}
              isCrowdRecommendation={false}
              currentFilter={currentFilter}
            />
            {(initialData.seoInfo?.h_one || initialData.seoInfo?.pc_pure_text) && (
              <div className="px-[15px] py-[20px] text-center">
                {initialData.seoInfo.h_one && (
                  <h1 className="text-left text-[16px] leading-[22px] font-bb mb-[10px]">
                    {initialData.seoInfo.h_one}
                  </h1>
                )}

                {initialData.seoInfo.pc_pure_text && (
                  <div className="relative">
                    <div
                      ref={seoDescRef}
                      className={`text-left text-[12px] leading-[18px] whitespace-pre-wrap ${
                        !isTextExpanded && isTextOverflow ? 'max-h-[180px] overflow-hidden' : ''
                      }`}
                      dangerouslySetInnerHTML={{ __html: initialData.seoInfo.pc_pure_text }}
                    />

                    {/* 展开/收起图标 */}
                    {isTextOverflow && (
                      <CollapseArrowDownOutlined
                        className={`w-[11px] mt-[10px] mx-auto cursor-pointer transition-transform ${
                          isTextExpanded ? 'rotate-180' : ''
                        }`}
                        onClick={() => setIsTextExpanded(!isTextExpanded)}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="right"
          width="335px"
          round={false}
          title={t('filters')}
          bodyClassName="!pt-0"
        >
          {renderGoodsFilter(true)}
        </Popup>
      </WapOnly>
    </div>
  );
}
